import type { SelectedTab } from 'features/navbar/types'
import type { ComponentType } from 'react'

export type CommandPaletteTargetKind =
  | 'page'
  | 'settings'
  | 'entity'
  | 'model'
  | 'document'

interface CommandPaletteTargetBase {
  id: string
  label: string
  kindLabel: string
  icon: ComponentType
  href: string
}

export type CommandPaletteTarget = CommandPaletteTargetBase &
  (
    | {
        kind: 'page'
        destination: SelectedTab
        /** Set when the row is about one asset shown on the page, not the page itself. */
        unitId?: string
      }
    | {
        kind: 'settings'
        destinationKey: string
        reuseMode?: 'focus-preserving-state' | 'focus-and-navigate'
      }
    | { kind: 'entity'; destination: SelectedTab }
    | { kind: 'model' }
    | { kind: 'document'; documentId: string }
  )

export type CommandPalettePageTarget = Extract<
  CommandPaletteTarget,
  { kind: 'page' }
>

const EXACT = 4
const PREFIX = 3
const WORD_PREFIX = 2
const SUBSTRING = 1
const NO_MATCH = 0

const normalize = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

/** Higher is better; 0 means the label does not match. An empty query matches everything. */
export function scoreLabelMatch(label: string, query: string): number {
  const normalizedQuery = normalize(query)
  if (!normalizedQuery) return SUBSTRING
  const normalizedLabel = normalize(label)
  if (normalizedLabel === normalizedQuery) return EXACT
  if (normalizedLabel.startsWith(normalizedQuery)) return PREFIX
  if (normalizedLabel.includes(` ${normalizedQuery}`)) return WORD_PREFIX
  if (normalizedLabel.includes(normalizedQuery)) return SUBSTRING
  return NO_MATCH
}

const KIND_ORDER: Record<CommandPaletteTargetKind, number> = {
  page: 0,
  settings: 1,
  entity: 2,
  model: 3,
  document: 4,
}

const compareLabels = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

/**
 * Only label matches survive, so the list narrows as the user types even
 * while a server search is still in flight. With no query the input order is
 * kept, so pages read in sidebar order.
 */
export function rankCommandPaletteTargets(
  targets: readonly CommandPaletteTarget[],
  query: string
): CommandPaletteTarget[] {
  if (!normalize(query)) return [...targets]
  return targets
    .map((target) => ({ target, score: scoreLabelMatch(target.label, query) }))
    .filter(({ score }) => score > NO_MATCH)
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score
      if (a.target.kind !== b.target.kind) {
        return KIND_ORDER[a.target.kind] - KIND_ORDER[b.target.kind]
      }
      return compareLabels(a.target.label, b.target.label)
    })
    .map(({ target }) => target)
}

/** Groups in ranked order of first appearance, so the best group leads the list. */
export function groupCommandPaletteTargets(
  targets: readonly CommandPaletteTarget[]
): Array<{ kindLabel: string; targets: CommandPaletteTarget[] }> {
  const groups = new Map<string, CommandPaletteTarget[]>()
  for (const target of targets) {
    const group = groups.get(target.kindLabel)
    if (group) group.push(target)
    else groups.set(target.kindLabel, [target])
  }
  return Array.from(groups, ([kindLabel, grouped]) => ({
    kindLabel,
    targets: grouped,
  }))
}
